import React, { useContext, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles, createTheme, ThemeProvider } from "@material-ui/core/styles";
import { IconButton } from "@material-ui/core";
import { MoreVert, Replay, GetApp, CloudUpload } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import UndoRoundedIcon from '@material-ui/icons/UndoRounded';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import Tooltip from '@material-ui/core/Tooltip';
import { green } from '@material-ui/core/colors';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, InputLabel, Select, MenuItem, CircularProgress, Typography, TextField } from '@material-ui/core';
import TakeOverTicketModal from "../TakeOverTicketModal";


const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		"& > *": {
			margin: theme.spacing(0.5),
		},
	},
	pdfButton: {
		color: theme.palette.primary.main,
	}
}));

const TicketActionButtonsCustom = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [pdfAvailable, setPdfAvailable] = useState(false);
	const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
	const [takeOverModalOpen, setTakeOverModalOpen] = useState(false);
	const [setores, setSetores] = useState([]);
	const [selectedSetor, setSelectedSetor] = useState('');
	const [loadingSetores, setLoadingSetores] = useState(false);
	const [uploadLoading, setUploadLoading] = useState(false);
	const [company, setCompany] = useState(null);
	const [documentName, setDocumentName] = useState('');
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);
	const { setCurrentTicket } = useContext(TicketsContext);

	const customTheme = createTheme({
		palette: {
		  	primary: green,
		}
	});

	useEffect(() => {
		setPdfAvailable(!!window.handleExportPdfGlobal);
		const interval = setInterval(() => {
			setPdfAvailable(!!window.handleExportPdfGlobal);
		}, 1000);
		
		// Buscar dados da empresa
		const fetchCompany = async () => {
			try {
				const response = await api.get(`/companies/${user.companyId}`);
				setCompany(response.data);
			} catch (error) {
				console.error("Erro ao buscar dados da empresa:", error);
			}
		};
		
		if (user?.companyId) {
			fetchCompany();
		}
		
		return () => clearInterval(interval);
	}, [user]);

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
				useIntegration: status === "closed" ? false : ticket.useIntegration,
				promptId: status === "closed" ? false : ticket.promptId,
				integrationId: status === "closed" ? false : ticket.integrationId
			});

			setLoading(false);
			if (status === "open") {
				setCurrentTicket({ ...ticket, code: "#open" });
			} else {
				setCurrentTicket({ id: null, code: null })
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const handleDownloadPdf = () => {
		if (window.handleExportPdfGlobal) {
			try {
				window.handleExportPdfGlobal();
			} catch (error) {
				console.error("Error generating PDF:", error);
				toastError("Erro ao gerar PDF. Tente novamente.");
			}
		}
	};

	const handleOpenUploadDialog = async () => {
		if (!company?.apikey || !company?.url_name) {
			toastError("Empresa não possui API Key ou URL configurada para upload externo.");
			return;
		}
		
		setUploadDialogOpen(true);
		setLoadingSetores(true);
		setSetores([]); // Limpar setores anteriores
		setDocumentName(`Ticket_${ticket.id}_${new Date().toISOString().split('T')[0]}`); // Nome padrão
		
		try {
			const response = await fetch(`https://${company.url_name}.prosipe.com/api/external/setores`, {
				method: 'GET',
				headers: {
					'X-API-Key': company.apikey,
					'Content-Type': 'application/json'
				}
			});
			
			if (!response.ok) {
				throw new Error('Erro ao buscar setores');
			}
			
			const data = await response.json();
			// Garantir que data é um array ou tem data.setores
			if (Array.isArray(data)) {
				setSetores(data);
			} else if (data && data.data && Array.isArray(data.data.setores)) {
				setSetores(data.data.setores);
			} else if (data && Array.isArray(data.setores)) {
				setSetores(data.setores);
			} else {
				throw new Error('Resposta da API não contém array de setores');
			}
		} catch (error) {
			console.error("Erro ao buscar setores:", error);
			toastError("Erro ao buscar setores. Verifique as configurações da empresa.");
			setSetores([]); // Garantir que setores é um array vazio em caso de erro
			setUploadDialogOpen(false);
		} finally {
			setLoadingSetores(false);
		}
	};

	const handleUploadPdf = async () => {
		if (!selectedSetor) {
			toastError("Selecione um setor.");
			return;
		}

		if (!documentName.trim()) {
			toastError("Informe o nome do documento.");
			return;
		}

			if (!window.handleExportPdfGlobal) {
			toastError("PDF não está disponível.");
			return;
		}

		setUploadLoading(true);
		toastError("Gerando PDF. Isso pode levar alguns segundos...", "info");		try {
			const setorSelecionado = setores.find(s => s.id.toString() === selectedSetor.toString());
			console.log("Iniciando geração e upload do PDF...");
			
			// Usar exatamente a mesma função do botão exportar PDF
			// Executar a função de exportar PDF (mesma do botão original)
			await window.handleExportPdfGlobal();
			
			// Aguardar um pouco para garantir que o PDF foi processado
			await new Promise(resolve => setTimeout(resolve, 1000));
			
			// Verificar se o PDF foi gerado e armazenado na variável global
			if (!window.lastGeneratedPdf) {
				throw new Error("Não foi possível capturar o PDF gerado. Por favor, tente novamente.");
			}
			
			// Gerar o blob a partir do PDF
			const capturedBlob = window.lastGeneratedPdf.output('blob');
			
			console.log("PDF capturado com sucesso, tamanho:", capturedBlob.size);

			// Preparar FormData para envio
			const formData = new FormData();
			formData.append("nome_documento", documentName.trim());
			formData.append("documento", capturedBlob, `${documentName.trim()}.pdf`);
			formData.append("setor_id", selectedSetor);
			formData.append("origem_sistema", "ProsipeChat");

			console.log("Enviando documento para:", `https://${company.url_name}.prosipe.com/api/external/upload-documento`);

			// Fazer o upload real
			const response = await fetch(`https://${company.url_name}.prosipe.com/api/external/upload-documento`, {
				method: 'POST',
				headers: {
					'X-API-Key': company.apikey
				},
				body: formData
			});

			if (!response.ok) {
				const errorText = await response.text();
				throw new Error(`Erro no upload: ${response.status} - ${errorText}`);
			}

			const result = await response.json();
			console.log("Upload realizado com sucesso:", result);
			
			toastError(`Documento "${documentName.trim()}" enviado com sucesso para o setor "${setorSelecionado?.nome}"!`, "success");
			
			setUploadDialogOpen(false);
			setSelectedSetor('');
			setDocumentName('');
			
		} catch (error) {
			console.error("Erro no upload:", error);
			toastError(`Erro ao enviar documento: ${error.message}`);
		} finally {
			setUploadLoading(false);
		}
	};

	const handleCloseUploadDialog = () => {
		setUploadDialogOpen(false);
		setSelectedSetor('');
		setDocumentName('');
		setSetores([]);
	};

	return (
		<div className={classes.actionButtons}>
			<Tooltip title={pdfAvailable ? "Exportar PDF" : "Carregando..."}>
				<span>
					<IconButton 
						onClick={handleDownloadPdf} 
						disabled={!pdfAvailable}
						className={pdfAvailable ? classes.pdfButton : ""}
					>
						<GetApp />
					</IconButton>
				</span>
			</Tooltip>
			<Tooltip title={pdfAvailable && company?.apikey && company?.url_name ? "Enviar para Sistema Externo" : "Não disponível"}>
				<span>
					<IconButton 
						onClick={handleOpenUploadDialog} 
						disabled={!pdfAvailable || !company?.apikey || !company?.url_name}
						className={pdfAvailable && company?.apikey && company?.url_name ? classes.pdfButton : ""}
					>
						<CloudUpload />
					</IconButton>
				</span>
			</Tooltip>
			{ticket.status === "closed" && (
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
			{ticket.status === "open" && (
				<>
					{ticket.userId && Number(ticket.userId) !== Number(user?.id) && (
						<Tooltip title="Assumir este atendimento para você e dar continuidade">
							<ButtonWithSpinner
								variant="contained"
								size="small"
								style={{
									backgroundColor: "#2e7d32",
									color: "#fff",
									fontWeight: 600,
									textTransform: "none",
									borderRadius: 8,
									padding: "4px 10px",
									marginRight: 6
								}}
								startIcon={<AssignmentIndIcon />}
								onClick={() => setTakeOverModalOpen(true)}
							>
								Assumir
							</ButtonWithSpinner>
						</Tooltip>
					)}
					<Tooltip title={i18n.t("messagesList.header.buttons.return")}>
						<IconButton onClick={e => handleUpdateTicketStatus(e, "pending", null)}>
							<UndoRoundedIcon />
						</IconButton>
					</Tooltip>
					<ThemeProvider theme={customTheme}>
						<Tooltip title={i18n.t("messagesList.header.buttons.resolve")}>
							<IconButton onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)} color="primary">
								<CheckCircleIcon />
							</IconButton>
						</Tooltip>
					</ThemeProvider>
					<IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
					/>
					<TakeOverTicketModal
						modalOpen={takeOverModalOpen}
						onClose={() => setTakeOverModalOpen(false)}
						ticket={ticket}
						onTakeOverSuccess={() => {
							setCurrentTicket({ ...ticket, userId: user?.id, user });
						}}
					/>
				</>
			)}
			{ticket.status === "pending" && (
				<ButtonWithSpinner
					loading={loading}
					size="small"
					variant="contained"
					color="primary"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.accept")}
				</ButtonWithSpinner>
			)}
			
			{/* Dialog para seleção de setor e upload */}
			<Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
				<DialogTitle>Enviar Documento para Sistema Externo</DialogTitle>
				<DialogContent>
					{loadingSetores ? (
						<div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
							<CircularProgress />
							<Typography style={{ marginLeft: '10px' }}>Carregando setores...</Typography>
						</div>
					) : (
						<>
							<TextField
								fullWidth
								margin="normal"
								label="Nome do Documento"
								value={documentName}
								onChange={(e) => setDocumentName(e.target.value)}
								disabled={uploadLoading}
								helperText="Nome que será dado ao documento no sistema externo"
							/>
							
							{Array.isArray(setores) && setores.length > 0 ? (
								<FormControl fullWidth margin="normal">
									<InputLabel>Selecione o Setor</InputLabel>
									<Select
										value={selectedSetor}
										onChange={(e) => setSelectedSetor(e.target.value)}
										disabled={uploadLoading}
									>
										{setores.map((setor) => (
											<MenuItem key={setor.id} value={setor.id}>
												{setor.nome}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							) : (
								<Typography color="textSecondary" style={{ padding: '20px', textAlign: 'center' }}>
									Nenhum setor disponível. Verifique as configurações da empresa.
								</Typography>
							)}
						</>
					)}
					{company && (
						<Typography variant="caption" display="block" style={{ marginTop: '10px' }}>
							Enviando para: {company.url_name}.prosipe.com
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseUploadDialog} disabled={uploadLoading}>
						Cancelar
					</Button>
					<Button 
						onClick={handleUploadPdf} 
						color="primary" 
						variant="contained"
						disabled={!selectedSetor || !documentName.trim() || uploadLoading || loadingSetores || !Array.isArray(setores) || setores.length === 0}
						startIcon={uploadLoading ? <CircularProgress size={20} /> : <CloudUpload />}
					>
						{uploadLoading ? 'Enviando...' : 'Enviar Documento'}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default TicketActionButtonsCustom;
